import React from 'react';
import { Activity, Users, Zap, AlertTriangle } from 'lucide-react';

export const SystemMetrics: React.FC = () => {
  const systemStats = {
    uptime: '99.97%',
    totalUsers: 1247,
    activeUsers: 892,
    dataProcessed: '2.4TB',
    transactionsToday: 1834,
    averageResponseTime: '45ms',
  };

  const alerts = [
    { type: 'warning', message: 'High CPU usage on DB server 2', time: '5 min ago' },
    { type: 'info', message: 'Scheduled maintenance in 2 hours', time: '15 min ago' },
    { type: 'error', message: 'Failed backup on storage cluster 3', time: '1 hour ago' },
  ];

  return (
    <div className="space-y-6">
      {/* System Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <Activity className="h-8 w-8 text-blue-500" />
            <span className="text-sm text-green-600 font-medium">Healthy</span>
          </div>
          <h3 className="text-2xl font-bold text-slate-800">{systemStats.uptime}</h3>
          <p className="text-slate-600">System Uptime</p>
        </div>

        <div className="bg-white rounded-lg p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <Users className="h-8 w-8 text-green-500" />
            <span className="text-sm text-blue-600 font-medium">+12%</span>
          </div>
          <h3 className="text-2xl font-bold text-slate-800">{systemStats.activeUsers}</h3>
          <p className="text-slate-600">Active Users</p>
        </div>

        <div className="bg-white rounded-lg p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <Zap className="h-8 w-8 text-orange-500" />
            <span className="text-sm text-green-600 font-medium">Fast</span>
          </div>
          <h3 className="text-2xl font-bold text-slate-800">{systemStats.averageResponseTime}</h3>
          <p className="text-slate-600">Avg Response Time</p>
        </div>
      </div>

      {/* System Alerts */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">System Alerts</h3>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {alerts.map((alert, index) => (
              <div key={index} className={`flex items-start space-x-3 p-4 rounded-lg border ${
                alert.type === 'error' ? 'border-red-200 bg-red-50' :
                alert.type === 'warning' ? 'border-yellow-200 bg-yellow-50' :
                'border-blue-200 bg-blue-50'
              }`}>
                <AlertTriangle className={`h-5 w-5 mt-0.5 ${
                  alert.type === 'error' ? 'text-red-500' :
                  alert.type === 'warning' ? 'text-yellow-500' :
                  'text-blue-500'
                }`} />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{alert.message}</p>
                  <p className="text-xs text-slate-500">{alert.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Resource Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Server Resources</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>CPU Usage</span>
                <span>68%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '68%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Memory Usage</span>
                <span>42%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: '42%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span>Storage Usage</span>
                <span>78%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div className="bg-orange-600 h-2 rounded-full" style={{ width: '78%' }}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Database Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-slate-600">Total Records</span>
              <span className="font-medium">2,847,392</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Queries/sec</span>
              <span className="font-medium">1,247</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Avg Query Time</span>
              <span className="font-medium">12ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Cache Hit Rate</span>
              <span className="font-medium text-green-600">98.4%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
